import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  SimpleGrid,
  Text,
} from '@mantine/core';
import { IconDots, IconEdit, IconPlayerStop } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { MedicationStatement } from '@medplum/fhirtypes';
import { useDateDisplay } from '@hooks/useDateDisplay';
import {
  getMedicationName,
  getDoseValue,
  getDoseUnit,
  getRoute,
  getAsNeeded,
  getDosageText,
  getNoteText,
  getStatusReasonText,
  isStopped,
  formatFrequency,
  parseEffectiveStart,
  parseEffectiveEnd,
} from '@/utils/medication.utils';
import type { MedicationTranslationKey } from '@consts/medication.consts';
import { LabeledField } from '@/components/LabeledField';

export type MedicationAction = 'edit' | 'stop' | 'remove';

export interface MedicationListItemProps {
  medication: MedicationStatement;
  onAction?: (action: MedicationAction) => void;
  isDeleting?: boolean;
  readonly?: boolean;
}

export const MedicationListItem = ({
  medication,
  onAction,
  isDeleting = false,
  readonly = false,
}: MedicationListItemProps) => {
  const { t } = useTranslation(['patient-pages']);
  const formatDate = useDateDisplay({
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // Use utility functions for safe nested access
  const medicationName =
    getMedicationName(medication) || t('medications.unknownMedication');
  const medicationIsStopped = isStopped(medication);

  // Extract individual fields
  const doseValue = getDoseValue(medication);
  const doseUnit = getDoseUnit(medication);
  const dose = doseValue
    ? `${doseValue}${doseUnit ? ` ${doseUnit}` : ''}`
    : undefined;

  const route = getRoute(medication);
  const routeDisplay = route?.text || route?.coding?.[0]?.display;

  const frequency = formatFrequency(
    medication,
    (key: MedicationTranslationKey) => t(key as any)
  );
  const asNeeded = getAsNeeded(medication);

  const startDate = parseEffectiveStart(medication);
  const endDate = parseEffectiveEnd(medication);

  const dosageText = getDosageText(medication);
  const noteText = getNoteText(medication);
  const statusReasonText = getStatusReasonText(medication);

  // Determine which actions are available
  // - Edit: available for active medications
  // - Stop: available for active medications that are already saved (have ID)
  const canEdit = !readonly && !medicationIsStopped && !isDeleting;
  const canStop =
    !readonly && !medicationIsStopped && Boolean(medication.id) && !isDeleting;
  const hasActions = canEdit || canStop;

  return (
    <Paper p="sm" withBorder radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box flex={1}>
          {/* Medication name */}
          <Text fw={600} size="sm" mb={8}>
            {medicationName}
          </Text>

          {/* Dosage fields in 2-column grid */}
          <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
            <LabeledField label={t('medications.doseValue')} value={dose} />
            <LabeledField label={t('medications.route')} value={routeDisplay} />
            <LabeledField
              label={t('medications.frequencyLabel')}
              value={frequency}
            />
            {asNeeded && (
              <Text size="xs" c="dimmed">
                <Text component="span" fw={500} c="dark">
                  {t('medications.asNeededLabel')}:
                </Text>{' '}
                {t('common:yes', { defaultValue: 'Yes' })}
              </Text>
            )}
            <LabeledField
              label={t('medications.onsetDate')}
              value={formatDate(startDate)}
            />
            <LabeledField
              label={t('medications.endDate')}
              value={formatDate(endDate)}
            />
          </SimpleGrid>

          {/* Full-width fields */}
          {dosageText && (
            <Box mt={4}>
              <LabeledField
                label={t('medications.dosageText')}
                value={dosageText}
              />
            </Box>
          )}
          {noteText && (
            <Box mt={4}>
              <LabeledField label={t('medications.notes')} value={noteText} />
            </Box>
          )}
          {medicationIsStopped && statusReasonText && (
            <Box mt={4}>
              <LabeledField
                label={t('medications.reason')}
                value={statusReasonText}
              />
            </Box>
          )}
        </Box>

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction?.('edit')}
                  >
                    {t('medications.actions.edit')}
                  </Menu.Item>
                )}
                {canStop && (
                  <Menu.Item
                    leftSection={<IconPlayerStop size={14} />}
                    onClick={() => onAction?.('stop')}
                    color="red"
                  >
                    {t('medications.actions.stop')}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>
    </Paper>
  );
};
